/**
 * Personalized Image Banner
 * Handles Salesforce Data 360 CDP integration for personalized banner experiences
 * Shows only the experience that matches the CDP API response
 */

class PersonalizedImageBanner {
  constructor(moduleElement) {
    this.module = moduleElement;
    this.sectionId = moduleElement.dataset.sectionId;
    this.maxWaitTime = parseInt(moduleElement.dataset.maxWaitTime) || 2000;
    this.cdpEnabled = moduleElement.dataset.cdpEnabled === 'true';
    this.cdpEnvironment = window.D360ThemeSettings?.selectedEnvironment || 'Development';
    this.personalizationPoint = moduleElement.dataset.personalizationPoint;
    this.fallbackExperience = moduleElement.dataset.fallbackExperience;
    this.mockResponse = moduleElement.dataset.mockResponse;

    // Get all banner experience blocks
    this.bannerBlocks = Array.from(this.module.querySelectorAll('[data-experience-id]'));

    // Personalization tracking properties
    this.currentPersonalizationData = null;
    this.isPersonalizedExperience = false;
    this.viewEventFired = false;
    this.intersectionObserver = null;

    // Resolve banner block up to 3 times, then hide section and stop
    this.maxResolveAttempts = 3;
    this.resolveRetryDelayMs = 500;
    this.resolveStopped = false;

    this.init();
  }

  /**
   * Initialize personalization process
   */
  async init() {
    if (!this.cdpEnabled) {
      // Hide skeleton when CDP is disabled
      const skeletonWrapper = this.module.querySelector('[data-skeleton]');
      if (skeletonWrapper) {
        skeletonWrapper.style.setProperty('display', 'none', 'important');
      }
      // When personalization is disabled, show only the fallback experience
      await this.resolveAndShowExperience(this.fallbackExperience);
      return;
    }

    // Clear existing matching experience cookie to ensure fallback takes precedence
    if (this.fallbackExperience) {
      const existingCookie = document.cookie.split('; ').find((row) => row.startsWith('d360_matching_experience='));
      if (existingCookie) {
        document.cookie = 'd360_matching_experience=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }

    // Add loading state to all blocks
    this.addLoadingState();

    try {
      const personalizationResult = await this.getPersonalizedExperience();

      // If no visitor cookie or not personalized, show fallback experience
      if (personalizationResult.experienceId === null || !personalizationResult.isPersonalized) {
        // Clear any existing personalization data to prevent events
        this.isPersonalizedExperience = false;
        this.currentPersonalizationData = null;
        await this.resolveAndShowExperience(this.fallbackExperience);
        return;
      }

      // Store personalization data for event tracking only for personalized experiences
      this.isPersonalizedExperience = personalizationResult.isPersonalized;
      this.currentPersonalizationData = personalizationResult.personalizationData;

      await this.resolveAndShowExperience(personalizationResult.experienceId);
    } catch {
      // Silent fallback in production
      this.isPersonalizedExperience = false;
      this.currentPersonalizationData = null;
      await this.resolveAndShowExperience(this.fallbackExperience);
    } finally {
      this.removeLoadingState();
    }
  }

  /**
   * Get personalized experience using shared D360 utilities
   */
  async getPersonalizedExperience() {
    if (!window.D360Utils) {
      console.error('D360 utilities not loaded');
      return this.fallbackExperience;
    }

    const config = {
      personalizationPointName: this.personalizationPoint,
      fallbackExperienceId: this.fallbackExperience,
      maxWaitTime: this.maxWaitTime,
      environment: this.cdpEnvironment,
      mockResponse: this.mockResponse,
      enableDebugLogging: true,
    };

    return await window.D360Utils.getPersonalizedExperience(config);
  }

  /**
   * Refresh banner block list (blocks may render after first paint)
   */
  refreshBannerBlocks() {
    this.bannerBlocks = Array.from(this.module.querySelectorAll('[data-experience-id]'));
  }

  /**
   * IDs to try when resolving which banner to show (requested, then fallback if different)
   */
  getExperienceIdsToTry(primaryExperienceId) {
    const ids = [];
    const primaryId = primaryExperienceId != null && primaryExperienceId !== '' ? primaryExperienceId.toString() : '';
    const fallbackId =
      this.fallbackExperience != null && this.fallbackExperience !== '' ? this.fallbackExperience.toString() : '';

    if (primaryId) {
      ids.push(primaryId);
    }
    if (fallbackId && fallbackId !== primaryId) {
      ids.push(fallbackId);
    }
    return ids;
  }

  findBannerForExperienceId(experienceId) {
    const requestedId = experienceId != null && experienceId !== '' ? experienceId.toString() : '';
    if (!requestedId) {
      return null;
    }
    return this.bannerBlocks.find((block) => block.dataset.experienceId === requestedId) || null;
  }

  /**
   * Try up to 3 times to match a banner block; then hide section and stop further checks
   */
  async resolveAndShowExperience(experienceId) {
    if (this.resolveStopped) {
      return;
    }

    const idsToTry = this.getExperienceIdsToTry(experienceId);

    if (idsToTry.length === 0) {
      this.hideSection();
      return;
    }

    for (let attempt = 1; attempt <= this.maxResolveAttempts; attempt++) {
      if (this.resolveStopped) {
        return;
      }

      this.refreshBannerBlocks();

      for (const id of idsToTry) {
        const matchingBanner = this.findBannerForExperienceId(id);
        if (matchingBanner) {
          if (id !== idsToTry[0]) {
            this.isPersonalizedExperience = false;
            this.currentPersonalizationData = null;
          }
          this.displayExperience(id, matchingBanner);
          return;
        }
      }

      if (attempt < this.maxResolveAttempts) {
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
          console.warn(
            `No banner found for Experience_ID(s) [${idsToTry.join(', ')}] (attempt ${attempt}/${this.maxResolveAttempts}). Retrying…`,
          );
        }
        await this.delay(this.resolveRetryDelayMs);
      }
    }

    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
      console.warn(
        `No banner found for Experience_ID(s) [${idsToTry.join(', ')}] after ${this.maxResolveAttempts} attempts. Hiding section.`,
      );
    }
    this.hideSection();
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Show one matched banner experience and hide all others (no recursion)
   */
  displayExperience(experienceId, matchingBanner) {
    const requestedId = experienceId.toString();

    this.bannerBlocks.forEach((block) => {
      if (block.dataset.experienceId === requestedId) {
        this.showBanner(block);
      } else {
        this.hideBanner(block);
      }
    });

    if (this.isPersonalizedExperience && this.currentPersonalizationData) {
      this.setupPersonalizationTracking(matchingBanner);
    }
  }

  /**
   * Hide entire personalized banner section and stop further resolve attempts
   */
  hideSection() {
    this.resolveStopped = true;
    this.isPersonalizedExperience = false;
    this.currentPersonalizationData = null;

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    this.refreshBannerBlocks();
    this.bannerBlocks.forEach((block) => this.hideBanner(block));

    const skeletonWrapper = this.module.querySelector('[data-skeleton]');
    if (skeletonWrapper) {
      skeletonWrapper.style.setProperty('display', 'none', 'important');
    }

    this.module.style.setProperty('display', 'none', 'important');

    const shopifySection = this.module.closest('.shopify-section');
    if (shopifySection) {
      shopifySection.style.setProperty('display', 'none', 'important');
    }
  }

  /**
   * Show a banner block and its parent wrapper
   */
  showBanner(bannerElement) {
    if (bannerElement) {
      bannerElement.style.setProperty('display', 'block', 'important');
      // Also show the parent wrapper
      const parentWrapper = bannerElement.closest('.banner-experience-wrapper');
      if (parentWrapper) {
        parentWrapper.style.setProperty('display', 'block', 'important');
      }
    }
  }

  /**
   * Hide a banner block and its parent wrapper
   */
  hideBanner(bannerElement) {
    if (bannerElement) {
      bannerElement.style.setProperty('display', 'none', 'important');
      // Also hide the parent wrapper to remove padding
      const parentWrapper = bannerElement.closest('.banner-experience-wrapper');
      if (parentWrapper) {
        parentWrapper.style.setProperty('display', 'none', 'important');
      }
    }
  }

  /**
   * Add loading state to all banners
   */
  addLoadingState() {
    this.module.classList.add('loading');
    // Don't set opacity here - let CSS handle the skeleton state
  }

  /**
   * Remove loading state from all banners
   */
  removeLoadingState() {
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      this.module.classList.remove('loading');

      // Explicitly hide the skeleton wrapper to override inline styles
      const skeletonWrapper = this.module.querySelector('[data-skeleton]');
      if (skeletonWrapper) {
        skeletonWrapper.style.setProperty('display', 'none', 'important');
      }
    }, 100);
  }

  /**
   * Set up personalization tracking for view and click events
   */
  setupPersonalizationTracking(bannerElement) {
    // Set up IntersectionObserver for view event
    this.setupIntersectionObserver(bannerElement);

    // Set up click event listeners for CTAs
    this.setupClickTracking(bannerElement);
  }

  /**
   * Set up IntersectionObserver to fire view event when banner enters viewport
   */
  setupIntersectionObserver(bannerElement) {
    if (this.viewEventFired || !this.currentPersonalizationData) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.viewEventFired) {
            this.fireViewEvent();
            this.viewEventFired = true;
            // Disconnect observer after first trigger
            if (this.intersectionObserver) {
              this.intersectionObserver.disconnect();
              this.intersectionObserver = null;
            }
          }
        });
      },
      {
        threshold: 0.5, // Fire when 50% of banner is visible
      },
    );

    this.intersectionObserver.observe(bannerElement);
  }

  /**
   * Set up click event listeners for all CTA elements in the banner
   */
  setupClickTracking(bannerElement) {
    if (!this.currentPersonalizationData) {
      return;
    }

    // Find all clickable elements: banner link, buttons, and any links
    const clickableElements = [];

    // Banner-level link
    if (bannerElement.dataset.bannerlink) {
      clickableElements.push(bannerElement);
    }

    // All buttons and links within the banner content
    const buttonsAndLinks = bannerElement.querySelectorAll('a.button, .banner__content a');
    clickableElements.push(...buttonsAndLinks);

    // Add click event listener to each clickable element
    clickableElements.forEach((element) => {
      element.addEventListener('click', () => {
        this.fireClickEvent();
      });
    });
  }

  /**
   * Fire personalization view event
   */
  fireViewEvent() {
    if (!this.currentPersonalizationData || !window.D360Utils) {
      return;
    }

    window.D360Utils.publishPersonalizationViewEvent(this.currentPersonalizationData);

    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
      console.log('Personalization view event fired:', this.currentPersonalizationData);
    }
  }

  /**
   * Fire personalization click event
   */
  fireClickEvent() {
    if (!this.currentPersonalizationData || !window.D360Utils) {
      return;
    }

    window.D360Utils.publishPersonalizationClickEvent(this.currentPersonalizationData);

    if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
      console.log('Personalization click event fired:', this.currentPersonalizationData);
    }
  }
}

// Initialize personalized banner when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const personalizedModules = document.querySelectorAll('personalized-banner-module');

  personalizedModules.forEach((module) => {
    new PersonalizedImageBanner(module);
  });
});

/**
 * Re-initialize when sections are dynamically loaded (for Shopify theme dynamic sections)
 */
document.addEventListener('shopify:section:load', (event) => {
  const personalizedModule = event.target.querySelector('personalized-banner-module');
  if (personalizedModule) {
    new PersonalizedImageBanner(personalizedModule);
  }
});

// Export for potential use by other scripts
window.PersonalizedImageBanner = PersonalizedImageBanner;
